import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

(() => {
  'use strict';

  if (document.documentElement.dataset.peregyCartFlowReady === 'true') return;
  document.documentElement.dataset.peregyCartFlowReady = 'true';

  const ADD_FORM_SELECTOR = 'form[action*="/cart/add"]';
  const ADD_TRIGGER_SELECTOR = [
    'button[name="add"]',
    '[data-add-btn]',
    '[data-sticky-btn]',
    '[data-peregy-quick-add] button[type="submit"]',
    '[data-lfp-quick-add]',
    '[data-lfp-qv-add]',
    '.lux-cart-recommendation__action',
  ].join(',');

  let latestTrigger = null;
  let latestIntentAt = 0;

  const rootPath = () => window.Shopify?.routes?.root || '/';
  const cartAddUrl = () => window.Theme?.routes?.cart_add_url || `${rootPath()}cart/add.js`;
  const cartUrl = () => window.Theme?.routes?.cart_url || `${rootPath()}cart`;

  const noteIntent = (trigger) => {
    if (!(trigger instanceof HTMLElement)) return;
    latestTrigger = trigger;
    latestIntentAt = Date.now();
    trigger.classList.remove('is-peregy-added', 'is-peregy-cart-error');
    trigger.classList.add('is-peregy-adding');
    trigger.setAttribute('aria-busy', 'true');
  };

  const finishTrigger = (didError = false) => {
    const trigger = latestTrigger;
    latestTrigger = null;
    if (!(trigger instanceof HTMLElement) || !trigger.isConnected) return;

    trigger.classList.remove('is-peregy-adding');
    trigger.removeAttribute('aria-busy');
    trigger.classList.add(didError ? 'is-peregy-cart-error' : 'is-peregy-added');
    window.setTimeout(() => {
      if (!trigger.isConnected) return;
      trigger.classList.remove('is-peregy-added', 'is-peregy-cart-error');
    }, didError ? 1200 : 900);
  };

  let cartCountSyncToken = 0;

  const updateVisibleCartCounts = (count) => {
    if (!Number.isFinite(count) || count < 0) return;

    document.querySelectorAll('[data-peregy-mobile-cart]').forEach((trigger) => {
      const badge = trigger.querySelector('.peregy-mobile-nav__badge');
      if (badge instanceof HTMLElement) {
        badge.dataset.count = String(count);
        badge.textContent = count > 0 ? String(count) : '';
      }
      const label = trigger.querySelector('.peregy-mobile-nav__label')?.textContent?.trim() || 'السلة';
      trigger.setAttribute('aria-label', `${label}، ${count} منتجات`);
    });

    document.querySelectorAll('[data-peregy-cart-drawer]').forEach((trigger) => {
      trigger.setAttribute('aria-label', `السلة، ${count} منتجات`);
    });

    document.querySelectorAll('cart-icon').forEach((icon) => {
      if (typeof icon.renderCartBubble === 'function') {
        icon.renderCartBubble(count, false);
        return;
      }

      const countNode = icon.querySelector('[ref="cartBubbleCount"]');
      const bubble = icon.querySelector('[ref="cartBubble"]');
      if (countNode instanceof HTMLElement) {
        countNode.textContent = count > 0 && count < 100 ? String(count) : '';
        countNode.classList.toggle('hidden', count === 0);
      }
      if (bubble instanceof HTMLElement) bubble.classList.toggle('visually-hidden', count === 0);
    });
  };

  const syncCartCountFromServer = async () => {
    const token = ++cartCountSyncToken;
    try {
      const response = await fetch(`${rootPath()}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Cart count refresh failed: ${response.status}`);
      const cart = await response.json();
      if (token !== cartCountSyncToken) return;
      updateVisibleCartCounts(Number(cart?.item_count ?? 0));
    } catch (error) {
      console.warn('[peregy-cart-flow] Cart count sync failed:', error);
    }
  };

  const animateCart = () => {
    document.querySelectorAll('[data-peregy-cart-drawer], [data-peregy-mobile-cart]').forEach((trigger) => {
      trigger.classList.remove('is-peregy-cart-updated');
      void trigger.offsetWidth;
      trigger.classList.add('is-peregy-cart-updated');
      window.setTimeout(() => trigger.classList.remove('is-peregy-cart-updated'), 650);
    });
  };

  const openCartDrawer = async () => {
    const drawer = document.getElementById('cart-drawer');
    if (!(drawer instanceof HTMLElement)) return false;

    if (typeof drawer.open !== 'function' && window.customElements?.whenDefined) {
      try {
        await Promise.race([
          customElements.whenDefined('theme-drawer'),
          new Promise((resolve) => window.setTimeout(resolve, 900)),
        ]);
      } catch (error) { /* fall through */ }
    }

    await new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
    if (typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }

    const dialog = drawer.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement) {
      if (!dialog.open && typeof dialog.showModal === 'function') dialog.showModal();
      drawer.setAttribute('open', '');
      return true;
    }
    return false;
  };

  const getSectionIds = () => {
    const ids = new Set(['cart-drawer-section']);
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) ids.add(component.dataset.sectionId);
    });
    return Array.from(ids);
  };

  const fetchCart = async () => {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (typeof cartItemsComponent?.fetchCartData === 'function') return cartItemsComponent.fetchCartData();

    const response = await fetch(`${cartUrl()}.json`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
    return response.json();
  };

  const submitAjaxCartForm = async (form, submitter) => {
    if (form.dataset.peregySubmitting === 'true') return;
    form.dataset.peregySubmitting = 'true';

    const trigger = submitter instanceof HTMLElement ? submitter : form.querySelector('[type="submit"]');
    noteIntent(trigger);
    if (trigger instanceof HTMLButtonElement) trigger.disabled = true;

    const formData = new FormData(form);
    formData.delete('return_to');
    formData.set('sections', getSectionIds().join(','));
    formData.set('sections_url', `${window.location.pathname}${window.location.search}`);

    const variantId = String(formData.get('id') || '');
    const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
    const deferred = CartLinesUpdateEvent.createPromise();
    let settled = false;

    form.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    }));

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(cartAddUrl(), {
        ...config,
        credentials: 'same-origin',
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const data = await response.json();

      if (!response.ok || data?.status) {
        const message = data?.description || data?.message || 'تعذر إضافة المنتج إلى السلة';
        settled = true;
        deferred.reject(new Error(message));
        form.dispatchEvent(new CartErrorEvent({
          error: message,
          code: 'INVALID',
          detail: { description: message },
        }));
        throw new Error(message);
      }

      const sections = { ...(data.sections || {}) };
      if (!sections['cart-drawer-section']) {
        sections['cart-drawer-section'] = await sectionRenderer.getSectionHTML(
          'cart-drawer-section',
          false,
          new URL(window.location.href)
        );
      }

      const ajaxCart = await fetchCart();
      if (form.hasAttribute('data-peregy-quick-view-form')) {
        document.dispatchEvent(new CustomEvent('peregy:quick-view-added', {
          detail: { variantId, itemCount: ajaxCart.item_count },
        }));
      }
      settled = true;
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          sections,
          items: ajaxCart.items,
          itemCount: ajaxCart.item_count,
          productId: variantId,
          source: 'peregy-global-cart-flow',
          sourceId: variantId,
          didError: false,
        },
      });
    } catch (error) {
      if (!settled) deferred.reject(error);
      finishTrigger(true);
      console.warn('[peregy-cart-flow] Add to cart failed:', error);
    } finally {
      delete form.dataset.peregySubmitting;
      if (trigger instanceof HTMLButtonElement && trigger.isConnected) trigger.disabled = false;
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.(ADD_TRIGGER_SELECTOR);
    if (trigger) noteIntent(trigger);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(ADD_FORM_SELECTOR)) return;

    const nativeProductForm = form.closest('product-form-component');
    if (nativeProductForm) {
      noteIntent(event.submitter || form.querySelector('[type="submit"]'));
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    submitAjaxCartForm(form, event.submitter);
  }, true);

  document.addEventListener('DOMContentLoaded', syncCartCountFromServer, { once: true });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) syncCartCountFromServer();
  });

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const action = event.detail?.action || event.action;
    const recentAddIntent = Date.now() - latestIntentAt < 5000;
    const shouldOpenDrawer = action === 'add' || recentAddIntent;

    event.promise
      ?.then((payload) => {
        const { detail } = payload || {};
        if (detail?.didError) {
          if (shouldOpenDrawer) finishTrigger(true);
          syncCartCountFromServer();
          return;
        }

        syncCartCountFromServer();
        if (!shouldOpenDrawer) return;

        finishTrigger(false);
        animateCart();
        openCartDrawer();
      })
      .catch(() => {
        if (shouldOpenDrawer) finishTrigger(true);
        syncCartCountFromServer();
      });
  });
})();
